import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useFetchPoolAndLineItemData } from "./TeamHelper";
import { ToastContainer, toast } from "react-toastify";
import { IoChevronBackOutline } from "react-icons/io5";
import TeamViewForm from "./TeamViewForm";
import { supabase } from "../Utilities/supabaseClient";
import moment from "moment-timezone";
import { logError } from "../Utilities/errorLogger";

const Team = () => {
  const { poolId, teamId } = useParams();
  const navigate = useNavigate();
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const [content, setContent] = useState(null);
  const [spinner, setSpinner] = useState(false);
  const [formError, setFormError] = useState("");

  const {
    poolData: hookPoolData,
    teamLineItem: hookTeamLineItem,
    loading,
    error,
    isOwner: hookIsOwner,
  } = useFetchPoolAndLineItemData(poolId ? Number(poolId) : null, refetchTrigger, teamId ? Number(teamId) : null);

  const showError = error || formError;

  const hasTournamentStarted = useCallback((pool) => {
    if (!pool?.first_tee_time || !pool?.time_zone) return false;
    const firstTeeTime = moment.tz(pool.first_tee_time, pool.time_zone);
    const now = moment().tz(pool.time_zone);
    return now.isAfter(firstTeeTime);
  }, []);

  const renderContent = useCallback(async () => {
    if (!hookPoolData) return null;

    try {
      const { data: playerTournaments, error: playerError } = await supabase
        .from("playersTournaments")
        .select(`
          id,
          player:playersFixture!playersTournaments_player_fkey (
            id, player_name, world_ranking_position
          ),
          tournament
        `)
        .eq("tournament", hookPoolData.tournament_fixture.id)
        .not("player", "is", null)
        .order("player(player_name)", { ascending: true });

      if (playerError) {
        toast.error(`Error fetching player tournaments: ${playerError.message}`, { theme: "dark" });
        throw playerError;
      }

      if (!playerTournaments || playerTournaments.length === 0) {
        return (
          <div className="max-w-4xl mx-auto px-4 py-6 md:pt-20">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center">
              <button onClick={() => navigate(-1)} className="text-gray-500 hover:text-gray-700 mb-4 flex items-center gap-1 mx-auto">
                <IoChevronBackOutline className="w-5 h-5" /> Back
              </button>
              <p className="text-gray-600">The tournament field has not been set yet. Check back on Monday of tournament week when the official field is announced.</p>
            </div>
          </div>
        );
      }

      return (
        <TeamViewForm
          records={hookTeamLineItem || []}
          isEditable={hookIsOwner && (!hasTournamentStarted(hookPoolData) || hookTeamLineItem?.[0]?.team?.owner?.can_create_late_team)}
          poolData={hookPoolData}
          setSpinner={setSpinner}
          setFormError={setFormError}
          setRefetchTrigger={setRefetchTrigger}
          playerTournaments={playerTournaments}
        />
      );
    } catch (error) {
      await logError({ filePath: "Team/Team.js", functionName: "renderContent", errorType: "Error", errorMessage: error.message, stackTrace: error.stack });
      return (
        <div className="max-w-4xl mx-auto px-4 py-6 md:pt-20">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center">
            <p className="text-red-600">Error loading tournament data. Please try again later.</p>
          </div>
        </div>
      );
    }
  }, [hookPoolData, hookTeamLineItem, hookIsOwner, hasTournamentStarted, navigate]);

  useEffect(() => {
    if (!loading && hookTeamLineItem) {
      renderContent().then(setContent);
    }
  }, [loading, hookTeamLineItem, renderContent]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6 md:pt-20">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="grid grid-cols-4 gap-3">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-16 bg-gray-200 rounded-xl" />)}
          </div>
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-14 bg-gray-200 rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  if (showError) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6 md:pt-20">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center">
          <button onClick={() => navigate(-1)} className="text-gray-500 hover:text-gray-700 mb-4 flex items-center gap-1 mx-auto">
            <IoChevronBackOutline className="w-5 h-5" /> Back
          </button>
          <p className="text-red-600">{showError}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <ToastContainer position="top-center" />
      {spinner && (
        <div className="fixed inset-0 bg-white/80 z-50 flex items-center justify-center">
          <div className="animate-spin h-10 w-10 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      )}
      {content}
    </>
  );
};

export default Team;
